import { Component, Input } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import AirDatepicker from 'air-datepicker';
import { isSameDay } from 'date-fns';
import { EnodeService } from 'src/app/services/enode/enode.service';
import { SmartChargingPlan } from 'src/app/services/enode/enode.types';
import { CustomLocaleNb, theTime } from 'src/app/utils/date-utils';

type SmartChargingPlanExtended = SmartChargingPlan & {
  finalStateText: string;
  startText: string;
  endText: string;
  chargeDurationText: string;
  savingsText: string;
  costText: string;
  unfinishedReasonText: string;
  wasOverridden?: boolean;
  wasUnplugged?: boolean;
  deadlineText?: string;
};

@Component({
  selector: 'app-event-log-modal',
  templateUrl: './event-log-modal.component.html',
  styleUrls: ['./event-log-modal.component.scss']
})
export class EventLogModalComponent {
  @Input() vehicleId: string;

  isLoading: boolean;
  chargingPlans: SmartChargingPlan[];
  plansByDay: {
    date: string;
    plans: SmartChargingPlanExtended[];
  }[];
  totalSavings: number;
  totalCost: number;
  thisMonth: string;
  plansExpandedState: { [key: string]: boolean; } = {};

  showMonthCal = false;
  selectedMonth: Date;
  maxDate: Date;
  minDate: Date;
  monthDatepicker: AirDatepicker<HTMLElement>;

  constructor(
    private _modalCtrl: ModalController,
    private _enodeService: EnodeService,
    private _alertCtrl: AlertController
  ) { }

  ngOnInit() {
    this.isLoading = true;
    this.thisMonth = new Date().toLocaleDateString('nb-NO', {
      month: 'long',
      year: 'numeric'
    });

    this.selectedMonth = new Date();
    this.maxDate = this.selectedMonth;
    this.minDate = new Date(2024, 0, 1);
    this.showMonthCal = false;

    this.fetchChargingPlans();
  }

  ngAfterViewInit() {
    this.monthDatepicker = new AirDatepicker('#airEnodeMonth', {
      startDate: this.selectedMonth,
      locale: CustomLocaleNb,
      maxDate: this.maxDate,
      minDate: this.minDate,
      range: false,
      toggleSelected: false,
      view: 'months',
      minView: 'months',
      autoClose: true,
      dateFormat: 'MMMM yyyy',
      onSelect: function(date: Date) {
        if (date['formattedDate']) {
          this.setSelectedDate(date['date']);
          if (this.selectedMonth != date['date']) {
            this.showHideCal();
          }
        }
      }.bind(this)
    });
    this.monthDatepicker.selectDate(this.selectedMonth);
  }

  setSelectedDate(date: Date) {
    if (date.getHours() == 0) {
      const dateWithHours = new Date(date);
      dateWithHours.setHours(dateWithHours.getHours() + 6);
      this.selectedMonth = dateWithHours;
    } else {
      this.selectedMonth = date;
    }
    // get data
    this.fetchChargingPlans();
  }

  fetchChargingPlans() {
    const period = theTime(this.selectedMonth).to(['YYYY', 'MM']);
    this.isLoading = true;
    this._enodeService
      .getSmartChargingPlansForPeriod(this.vehicleId, period)
      .subscribe({
        next: (plans: SmartChargingPlan[]) => {
          this.chargingPlans = [...plans]
            .sort((a, b) => {
              const aDate = new Date(a.lastChanged);
              const bDate = new Date(b.lastChanged);
              return aDate > bDate ? -1 : aDate < bDate ? 1 : 0;
            })
            .filter((plan) => plan.endedAt !== null);

          this.chargingPlans.forEach((plan) => {
            this.plansExpandedState[plan.id] = false;
          });

          this.totalSavings = this.chargingPlans
            .filter(
              (x) =>
                x.sessionEstimatedSavings !== null ||
                (this.didFinish(x) && x.smartCost !== null)
            )
            .reduce((acc, plan) => {
              return (
                acc +
                (plan.sessionEstimatedSavings ??
                  plan.nonSmartCost - plan.smartCost ??
                  0)
              );
            }, 0);
          this.totalCost = this.chargingPlans
            .filter(
              (x) =>
                x.sessionCostSum !== null ||
                (this.didFinish(x) && x.smartCost !== null)
            )
            .reduce((acc, plan) => {
              return acc + (plan.sessionCostSum ?? plan.smartCost ?? 0);
            }, 0);

          this.plansByDay = this.chargingPlans.reduce((acc, plan) => {
            const planDate = new Date(plan.endedAt);
            const date = isSameDay(planDate, new Date())
              ? 'I dag'
              : planDate.toLocaleDateString('nb-NO', {
                weekday: 'long',
                day: 'numeric',
                month: 'long'
              });
            const planExtended = {
              ...plan,
              finalStateText: this.getFinalState(plan),
              startText: plan.startConfirmedAt
                ? this.getTime(plan.startConfirmedAt)
                : 'Ikke startet',
              endText: plan.endedAt ? this.getTime(plan.endedAt) : 'Ukjent',
              chargeDurationText: this.getChargeDuration(plan),
              savingsText: this.getSavings(plan),
              costText: this.getCost(plan),
              usageKwText: this.getKwUsage(plan),
              meanPriceText: this.getMeanPrice(plan),
              unfinishedReasonText: this.getUnfinishedReason(plan),
              deadlineText: this.getDeadlineText(plan),
              overriddenText: this.getSmartOverrideText(plan),
              wasUnplugged: plan.finalState === 'PLAN:ENDED:UNPLUGGED'
            };
            const existingDate = acc.find((item) => item.date === date);
            if (existingDate) {
              existingDate.plans.push(planExtended);
            } else {
              acc.push({ date, plans: [planExtended] });
            }
            return acc;
          }, [] as { date: string; plans: SmartChargingPlanExtended[]; }[]);
          this.isLoading = false;
        },
        error: (err) => {
          console.log('err', err);
          this.isLoading = false;
        }
      });
  }

  getTime(date: string) {
    return new Date(date).toLocaleTimeString('nb-NO', {
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric'
    });
  }

  getChargeDuration(plan: SmartChargingPlan) {
    if (!plan.startConfirmedAt) {
      return 'Ukjent';
    }
    const duration = Math.abs(
      new Date(plan.endedAt).getTime() -
      new Date(plan.startConfirmedAt).getTime()
    );
    const hours = Math.floor(duration / 1000 / 60 / 60);
    const minutes = Math.floor((duration / 1000 / 60 / 60 - hours) * 60);
    const seconds = Math.floor(
      ((duration / 1000 / 60 / 60 - hours) * 60 - minutes) * 60
    );
    return hours ? `${hours}t ${minutes}m` : `${minutes}m ${seconds}s`;
  }

  getFinalState(plan: SmartChargingPlan) {
    const state = plan.finalState;
    if (state === 'PLAN:ENDED:FINISHED') {
      if (plan.failureCondition !== null) {
        return 'Plan fullført*';
      }
      return 'Plan fullført';
    }
    if (state === 'PLAN:ENDED:UNPLUGGED') {
      return 'Lader frakoblet';
    }
    if (state === 'PLAN:ENDED:FAILED') {
      return 'Plan feilet';
    }
    if (state === 'PLAN:ENDED:DISABLED') {
      return 'Plan deaktivert';
    }
    if (state === 'PLAN:ENDED:DEADLINE_CHANGED') {
      return 'Frist endret';
    }
    if (state === null) {
      return 'Plan ikke ferdig';
    }
    return 'Ukjent';
  }

  getUnfinishedReason(plan: SmartChargingPlan) {
    const state = plan.finalState;
    if (state === 'PLAN:ENDED:UNPLUGGED') {
      return 'Planen ble avbrutt fordi laderen ble frakoblet';
    }
    if (state === 'PLAN:ENDED:DEADLINE_CHANGED') {
      return 'Planen ble avbrutt fordi det ble satt en ny tidsfrist';
    }
    if (state === 'PLAN:ENDED:DISABLED') {
      return 'Smartlading ble deaktivert før planen var ferdig';
    }
    if (state === 'PLAN:ENDED:FAILED') {
      const condition = plan.failureCondition;
      if (condition === 'STOP_FAILED') {
        return 'Stopp av lading feilet';
      }
      if (condition === 'START_FAILED') {
        return 'Start av lading feilet';
      }
      if (condition === 'FINISHED_LATE') {
        return 'Planen fullførte etter fristen';
      }
      if (condition === 'CHARGE_INTERRUPTED') {
        return 'Planen feilet fordi ladingen ble avbrutt';
      }
    }
    if (state === 'PLAN:ENDED:FINISHED' && plan.failureCondition !== null) {
      if (plan.failureCondition === 'FINISHED_LATE') {
        return 'Planen fullførte senere enn den angitte fristen, kostnadene er beregnet ut fra den opprinnelige fristen';
      }
    }
    if (plan.externalstartCreatedAt !== null) {
      return '';
    }
    return 'En ukjent feil oppsto';
  }

  getSmartOverrideText(plan: SmartChargingPlan) {
    if (plan.externalstartCreatedAt !== null) {
      const start = new Date(plan.externalstartCreatedAt);
      const end =
        plan.externalstartEndedAt && new Date(plan.externalstartEndedAt);
      if (start && end) {
        return `Ladeplanen ble overstyrt med manuell lading mellom ${start.toLocaleDateString(
          'nb-NO',
          {
            weekday: 'short',
            hour: '2-digit',
            minute: '2-digit'
          }
        )} og ${end.toLocaleDateString('nb-NO', {
          weekday: 'short',
          hour: '2-digit',
          minute: '2-digit'
        })}
                .`;
      }
      return `Ladeplanen ble overstyrt med manuell lading som startet ${start.toLocaleDateString(
        'nb-NO',
        {
          weekday: 'short',
          hour: '2-digit',
          minute: '2-digit'
        }
      )}.`;
    }
    return undefined;
  }

  getSavings(plan: SmartChargingPlan) {
    if (plan.sessionEstimatedSavings !== null) {
      return this.formatLocaleNumber(plan.sessionEstimatedSavings) + ' kr';
    } else if (this.didFinish(plan) && plan.smartCost !== null) {
      return (
        this.formatLocaleNumber(plan.nonSmartCost - plan.smartCost) + ' kr'
      );
    }
    return 'Mangler priser';
  }

  getCost(plan: SmartChargingPlan) {
    if (plan.sessionCostSum !== null) {
      return this.formatLocaleNumber(plan.sessionCostSum) + ' kr';
    } else if (this.didFinish(plan) && plan.smartCost !== null) {
      return this.formatLocaleNumber(plan.smartCost) + ' kr';
    }
    return 'Mangler priser';
  }

  getKwUsage(plan: SmartChargingPlan) {
    if (plan.sessionKwhSum !== null && plan.sessionKwhSum > 0) {
      return this.formatLocaleNumber(plan.sessionKwhSum) + ' kWh';
    }
    return 'Mangler data';
  }

  getMeanPrice(plan: SmartChargingPlan) {
    if (plan.sessionPriceMean !== null) {
      return this.formatLocaleNumber(plan.sessionPriceMean) + ' kr / kWh';
    }
    return 'Mangler data';
  }

  getDeadlineText(plan: SmartChargingPlan) {
    if (plan.deadline) {
      return this.getTime(plan.deadline);
    }
    return 'Mangler data';
  }

  didFinish(plan: SmartChargingPlan) {
    return (
      plan.endedAt !== null &&
      plan.finalState === 'PLAN:ENDED:FINISHED' &&
      plan.startConfirmedAt !== null
    );
  }

  didFinishWithError(plan: SmartChargingPlan) {
    return (
      plan.finalState === 'PLAN:ENDED:FINISHED' &&
      plan.failureCondition !== null
    );
  }

  toggleIsPlanExpanded(plan: SmartChargingPlan) {
    this.plansExpandedState[plan.id] = !this.plansExpandedState[plan.id];
  }

  async openHelpCost() {
    const alert = await this._alertCtrl.create({
      mode: 'ios',
      header: 'Hvordan beregnes besparelsen?',
      message:
        'På dager med store prisvariasjoner kan du spare penger ved å flytte lading til de billigste timene. Hvor mye du sparer, regnes ut som differansen mellom hva det ville kostet å lade bilen fra det tidspunktet den ble koblet til laderen, og hva det endte opp med å koste å lade bilen etter at smartladingsalgoritmen utsatte lading til de billigste timene på døgnet. Vi tar forbehold om feil.',
      buttons: ['Ok']
    });
    await alert.present();
  }

  cancel() {
    this._modalCtrl.dismiss();
  }

  formatLocaleNumber(num: number) {
    return num
      .toLocaleString('nb-NO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
      .replace('.', ',');
  }

  showHideCal() {
    this.showMonthCal = !this.showMonthCal;
  }
}
